import React from 'react';
import { Link } from 'react-router-dom';

import DocsLayout, {
  PromptBlock,
  Callout,
  pagerFor,
} from 'Containers/MCP/docs/DocsLayout';
import { TOOL_COUNT, RESOURCE_COUNT } from 'data/mcpTools';

const PATH = '/mcp/docs/first-session';
const { prev: PREV, next: NEXT } = pagerFor(PATH);

const TOC = [
  { href: '#prompts', label: 'Prompts to try' },
  { href: '#confirmations', label: 'Before anything is written' },
  { href: '#habits', label: 'Habits that help' },
];

const EXAMPLE_PROMPTS = [
  {
    prompt: 'Show me the documents in my Vocable library that are still in progress.',
    detail: (
      <>
        Runs <code className="mcp-docs__code-inline">list_documents</code>. A good
        first call because it only reads.
      </>
    ),
  },
  {
    prompt:
      'Read my brand voice, then draft a LinkedIn post about our new pricing page in that voice.',
    detail: (
      <>
        Pulls <code className="mcp-docs__code-inline">get_brand_context</code>{' '}
        first, so the draft follows your guidelines rather than a generic tone.
      </>
    ),
  },
  {
    prompt:
      'Research the keyword "ai content workflow" and tell me whether it is worth targeting.',
    detail: (
      <>
        Runs <code className="mcp-docs__code-inline">keyword_research</code> for
        volume, competition and related terms.
      </>
    ),
  },
  {
    prompt:
      'Create a content brief for a blog post on onboarding checklists, aimed at marketing managers.',
    detail: (
      <>
        Uses <code className="mcp-docs__code-inline">generate_brief</code> and{' '}
        <code className="mcp-docs__code-inline">create_brief</code>. Because it
        writes to your account, Claude should summarise the brief and ask you to
        confirm before saving it.
      </>
    ),
  },
];

const FirstSession = () => (
  <DocsLayout
    eyebrow="Using it"
    title="Your first session"
    lede={
      <>
        The connector is live the moment you have authorised it. What is left is
        working out loud: telling the agent which brand to act on, then asking
        for something real. Nothing here is setup you can get wrong &mdash; it is
        the same conversation you will have every day.
      </>
    }
    stats={[
      { value: `${TOOL_COUNT}`, label: 'tools available' },
      { value: `${RESOURCE_COUNT}`, label: 'brand resources' },
    ]}
    toc={TOC}
    prev={PREV}
    next={NEXT}
  >
    <h2 id="prompts">Prompts to try</h2>
    <p>
      Ask for an outcome rather than a tool. Each of these maps onto real tools,
      and the first one only reads, which makes it a safe way to prove the
      connection end to end:
    </p>
    <ul className="mcp-docs__cards">
      {EXAMPLE_PROMPTS.map((example) => (
        <li className="mcp-docs__card mcp-docs__card--quote" key={example.prompt}>
          <h3>&ldquo;{example.prompt}&rdquo;</h3>
          <p>{example.detail}</p>
        </li>
      ))}
    </ul>
    <p className="mcp-docs__after-cards">
      All {TOOL_COUNT} tools are listed in the{' '}
      <Link to="/mcp/docs/tools">tool reference</Link>, and the read-only brand
      context the agent can pull in is described under{' '}
      <Link to="/mcp/docs/resources">resources</Link>.
    </p>

    <h2 id="confirmations">Before anything is written</h2>
    <p>
      Tools split into two kinds, and the difference matters more than any
      individual tool name:
    </p>
    <ul>
      <li>
        <strong>Reads</strong> &mdash; listing documents, searching your library,
        reading brand context, keyword research. These are safe to run freely.
      </li>
      <li>
        <strong>Writes</strong> &mdash; creating or updating documents and briefs,
        sharing with teammates, publishing to a channel. These are written to
        describe what they are about to do and ask you to confirm first.
      </li>
    </ul>
    <p>
      Publishing is the one to watch: it pushes to a live channel such as
      WordPress or LinkedIn. Read back what the agent is about to send before you
      approve it, the same way you would check a draft in the app.
    </p>

    <h2 id="habits">Habits that help</h2>
    <ul>
      <li>
        <strong>Name the brand when you switch context.</strong> If you work
        across brands, say which one you mean rather than assuming the agent
        remembered from yesterday.
      </li>
      <li>
        <strong>Ask it to read before it writes.</strong> &ldquo;Read my brand
        voice first&rdquo; produces noticeably better drafts than asking for the
        draft alone.
      </li>
      <li>
        <strong>Let it search your own library.</strong> Semantic search over
        your existing content is usually a better source than the model&rsquo;s
        general knowledge about your market.
      </li>
      <li>
        <strong>Keep approvals deliberate.</strong> If you set the connector to
        always allow, you lose the pause before a publish.
      </li>
    </ul>

    <h2>Where to go next</h2>
    <ul className="mcp-docs__linkcards">
      <li>
        <Link to="/mcp/docs/tools">
          <strong>Tool reference</strong>
          <span>All {TOOL_COUNT} tools with their arguments and results.</span>
        </Link>
      </li>
      <li>
        <Link to="/mcp/docs/resources">
          <strong>Resources</strong>
          <span>
            The {RESOURCE_COUNT} read-only brand resources an agent can attach.
          </span>
        </Link>
      </li>
      <li>
        <Link to="/mcp/docs/troubleshooting">
          <strong>Troubleshooting &amp; FAQ</strong>
          <span>What each error means, and how to fix it.</span>
        </Link>
      </li>
    </ul>
  </DocsLayout>
);

export default FirstSession;
